// @system — Default text/copy for all pages. Overwritten during template sync.
// Products override in @custom/text/index.js — deep-merged at build time.
import { admin } from '../../../app/config/@system/text/admin.js'
import { blog } from '../../../app/config/@system/text/blog.js'

/**
 * Recursive partial — what an override layer may declare: any subset of leaves,
 * arrays always whole (deepMerge replaces arrays). Candidate for src/types/props.d.ts.
 * @template T
 * @typedef {{ [K in keyof T]?: T[K] extends readonly unknown[] ? T[K] : T[K] extends object ? DeepPartial<T[K]> : T[K] }} DeepPartial
 */

/**
 * Landing pricing card (text.landing.plans[]; with none, LandingPage and PricingSection fall back to
 * brand.json pricing.plans, then show no pricing section).
 * @typedef {object} LandingPlanText
 * @property {string} name
 * @property {string} price          display string ('$29', 'Custom')
 * @property {string} period         '/month' | 'forever' | ''
 * @property {string} description
 * @property {string[]} features
 * @property {string} cta
 * @property {string} ctaLink        route or mailto:
 * @property {boolean} [highlighted]
 */

/** @typedef {{ text: string, author: string, role: string, initials: string }} LandingTestimonialText */
/** @typedef {{ q: string, a: string }} FaqItemText */
/** @typedef {{ value: string, label: string }} StatText */

/**
 * @typedef {object} LandingText
 * @property {{ title: string, subtitle: string, cta: string, ctaSecondary: string, ctaSecondaryLink: string }} hero
 * @property {StatText[]} stats
 * @property {string[]} logoCompanies
 * @property {{ rating: string, ratingLabel: string, heading: string }} socialProof
 * @property {LandingTestimonialText[]} testimonials
 * @property {FaqItemText[]} faq
 * @property {LandingPlanText[]} plans
 * @property {string} blogTitle
 * @property {string} blogSubtitle
 * @property {string} blogCta
 * @property {string} blogCtaLink
 */

/**
 * All copy `import { text } from '@/config'` resolves to (this file deep-merged with
 * @custom/text). Every key is present because this file defines it.
 * @typedef {object} SiteText
 * @property {typeof admin} admin   Admin area copy (Staff & roles page, access-denied screen)
 * @property {{ scrollHint: string }} a11y
 * @property {LandingText} landing
 * @property {{ title: string, subtitle: string }} pricing
 * @property {{ loginTitle: string, loginSubtitle: string, registerTitle: string, registerSubtitle: string }} auth
 * @property {typeof blog} blog     Public blog pages + admin blog area — app/config/@system/text/blog.js
 * @property {{ tagline: string | null, copyright: string | null }} footer   null → derived from info
 * @property {{ title: string, subtitle: string, cta: string }} notFound
 */

/** What @custom/text/index.js may declare. */
/** @typedef {DeepPartial<SiteText>} SiteTextOverrides */

/** @type {SiteText} */
export const text = {
  // Admin area copy (Staff & roles page, access-denied screen) — app/config/@system/text/admin.js
  admin,
  // Accessibility strings shared by @system components (docs/ACCESSIBILITY.md)
  a11y: {
    scrollHint: 'Scroll sideways to see more',
  },
  landing: {
    hero: {
      title: 'Build faster. Launch sooner.',
      subtitle: 'Create your account, invite your team and get started in minutes.',
      cta: 'Get Started Free',
      ctaSecondary: 'View Pricing',
      ctaSecondaryLink: '/#pricing',
    },
    // Empty until the product has real numbers — the landing page hides the section
    // (never fabricated usage figures on a customer site).
    stats: [],
    // Empty until the product has real customers to show — the landing page hides the
    // logo strip (never fabricated partner names on a customer site).
    logoCompanies: [],
    // The logo strip heading; the names come from logoCompanies (the product's own, or none).
    socialProof: {
      rating: '4.9/5',
      ratingLabel: 'Rated ',
      heading: 'Trusted by',
    },
    testimonials: [],
    faq: [],
    plans: [],
    blogTitle: 'Latest from the blog',
    blogSubtitle:
      'Guides, engineering deep-dives, and product updates from the team.',
    blogCta: 'View all articles',
    blogCtaLink: '/blog',
  },
  pricing: {
    title: 'Simple, transparent pricing',
    subtitle: 'Start free. Upgrade when you need more.',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to your account',
    registerTitle: 'Create your account',
    registerSubtitle: 'Start your free trial today',
  },
  // Blog copy (public pages + /app/admin/blog) — app/config/@system/text/blog.js
  blog,
  footer: {
    tagline: null,
    copyright: null,
  },
  notFound: {
    title: 'Page not found',
    subtitle: 'The page you are looking for does not exist or has been moved.',
    cta: 'Go home',
  },
}
